// TEMPORARY Vue 2 mixin for the 5 document Pay pages. Requires the page to
// expose: form.payment_type, form.pay_reference, price.getMoney, data.net_price,
// change, overdue, loading. It only READS the page; the page keeps owning all
// data. Set `customerDisplayDirection: 'refund'` in a page's own data() for a
// refund flow (debit note) - every other page defaults to 'collect'.
//
// Announces the page's presence (so the display is not released while staff
// take payment), publishes a live settlement summary (channel, amount
// received, change/owed) as the cashier fills the form, and after the payment
// API succeeds switches the display to THANK YOU - the last settlement
// published rides along onto that screen automatically.

import { enterDocumentPage, getPublisher, leaveDocumentPage } from './session';
import { branchIdentity } from './documentAdapter';
import { fetchEvaluationSession, isEvaluationSessionEnabled, PAYMENT_REFRESH_TIMEOUT_MS } from './evaluationSession';

const SETTLEMENT_DEBOUNCE_MS = 60;
const CHANNEL_BY_PAYMENT_TYPE = { 1: 'cash', 2: 'transfer', 3: 'credit', 4: 'cheque' };

export default {
  data: () => ({
    customerDisplayDirection: 'collect'
  }),

  computed: {
    // Depends on reactive state the PAGE owns (form/price/data/computed
    // change & overdue); recomputes whenever any of it changes.
    customerDisplaySettlementInput() {
      return {
        channel: CHANNEL_BY_PAYMENT_TYPE[this.form.payment_type] || 'cash',
        direction: this.customerDisplayDirection,
        received: Number(this.price.getMoney) || 0,
        change: Number(this.change) || 0,
        overdue: Number(this.overdue) || 0,
        reference: this.form.pay_reference || ''
      };
    }
  },

  watch: {
    customerDisplaySettlementInput() {
      this.customerDisplayScheduleSettlement();
    },
    loading(isLoading) {
      // The initial fetch just finished: publish the settlement now that
      // price.getMoney/data.net_price reflect the real document.
      if (!isLoading) this.customerDisplayScheduleSettlement();
    }
  },

  created() {
    enterDocumentPage();
    this.customerDisplaySettlementTimer = null;
    // Capture before any pay response can replace the page's document_id with
    // a newly created Receipt/Invoice id.
    this.customerDisplayOriginalDocumentId = String(
      (this.$route && this.$route.params && this.$route.params.id)
      || (this.form && this.form.document_id)
      || ''
    );
    getPublisher().associateDocument(this.customerDisplayOriginalDocumentId);
  },

  beforeDestroy() {
    clearTimeout(this.customerDisplaySettlementTimer);
    leaveDocumentPage(this.customerDisplayBranch());
  },

  methods: {
    customerDisplayBranch() {
      return branchIdentity(this.customerDisplaySelectedBranch());
    },

    customerDisplaySelectedBranch() {
      const store = this.$store && this.$store.state;
      const selectedId = store && store.selected_branch && store.selected_branch._id;
      const branches = (store && store.list && store.list.branchs) || [];
      return branches.find((branch) => branch._id === selectedId);
    },

    customerDisplayScheduleSettlement() {
      clearTimeout(this.customerDisplaySettlementTimer);
      this.customerDisplaySettlementTimer = setTimeout(
        () => this.customerDisplayPublishSettlement(),
        SETTLEMENT_DEBOUNCE_MS
      );
    },

    customerDisplayPublishSettlement() {
      // Half-loaded data (before getData() resolves) must never reach the
      // customer, same rule as the document mixin.
      if (this.loading) return;
      getPublisher().setSettlement(this.customerDisplaySettlementInput);
    },

    customerDisplayUsesEvaluationSession() {
      const routeName = this.$route && this.$route.name;
      return routeName === 'document-quotation-pay' || routeName === 'document-invoice-pay';
    },

    // Call ONLY from the success path of /document/pay_document. Refreshing
    // after payment picks up paid_at/expires_at and a rating that may have been
    // submitted while the cashier was taking payment. Failure is deliberately
    // non-fatal and fail-closed: only this bounded refresh may promote the
    // immediate THANK_YOU to RATING; cached pre-payment state is not enough.
    async customerDisplayPaymentSucceeded(originalDocumentId) {
      const documentId = String(originalDocumentId || this.customerDisplayOriginalDocumentId || '').trim();
      const publisher = getPublisher();
      const branch = this.customerDisplayBranch();
      // Never hold the pre-payment document/settlement screen open while an
      // optional lookup is pending. THANK_YOU is immediate and remains the
      // fail-closed result unless a bounded refresh proves RATING is valid.
      publisher.showThankYou(branch);
      if (!this.customerDisplayUsesEvaluationSession() || !documentId
          || !isEvaluationSessionEnabled(this.customerDisplaySelectedBranch())) {
        if (documentId) publisher.setRatingSession(documentId, null);
        return;
      }
      try {
        const session = await fetchEvaluationSession(
          this,
          documentId,
          { timeout: PAYMENT_REFRESH_TIMEOUT_MS }
        );
        if (!isEvaluationSessionEnabled(this.customerDisplaySelectedBranch())) {
          publisher.setRatingSession(documentId, null);
          return;
        }
        publisher.setRatingSession(documentId, session);
      } catch (error) {
        // A cached pre-payment session does not prove that payment opened a
        // valid rating window. The immediate THANK_YOU therefore stays put
        // when this bounded refresh rejects or times out.
        return;
      }
      publisher.paymentSucceeded(branch, documentId);
    }
  }
};
